import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CircleCheck,
    ChevronDown,
    ChevronRight,
    TriangleAlert,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import LoadingRing from '../ui-components/LoadingRing';
import {
    getActorUsers,
    type ActorDepartmentGroup,
} from '../../services/api/actorService';
import { useToast } from '../../hooks/use-toast';
import { computeAlignment } from '../../utils/permissionAlignment';

interface ActorUsersPanelProps {
    actorId: string;
    activeDeptsOnly: boolean;
}

export default function ActorUsersPanel({
    actorId,
    activeDeptsOnly,
}: ActorUsersPanelProps) {
    const { toast } = useToast();
    const [loading, setLoading] = useState(true);
    const [departments, setDepartments] = useState<ActorDepartmentGroup[]>([]);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getActorUsers(actorId);
            setDepartments(data.departments);
        } catch {
            toast({ title: 'Failed to load users', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    }, [actorId, toast]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const filteredDepartments = useMemo(() => {
        if (!activeDeptsOnly) return departments;
        return departments.filter((d) => {
            if (!d.department.end_date) return true;
            return new Date(d.department.end_date) > new Date();
        });
    }, [departments, activeDeptsOnly]);

    const toggleCollapse = (deptId: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(deptId)) next.delete(deptId);
            else next.add(deptId);
            return next;
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingRing size="small" />
            </div>
        );
    }

    if (filteredDepartments.every((d) => d.users.length === 0)) {
        return (
            <p className="py-4 text-center text-sm text-muted-foreground">
                No users assigned to this actor.
            </p>
        );
    }

    return (
        <div className="space-y-3">
            {filteredDepartments.map((dept) => {
                const isCollapsed = collapsed.has(dept.department._id);

                return (
                    <div
                        key={dept.department._id}
                        className="rounded-lg border border-subtle"
                    >
                        <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-muted"
                            onClick={() => toggleCollapse(dept.department._id)}
                        >
                            {isCollapsed ? (
                                <ChevronRight className="h-4 w-4" />
                            ) : (
                                <ChevronDown className="h-4 w-4" />
                            )}
                            {dept.department.name}
                            <Badge variant="secondary" className="ml-1">
                                {dept.users.length}
                            </Badge>
                            <Badge
                                variant="outline"
                                className={`ml-auto text-[10px] ${
                                    dept.templateSource === 'department'
                                        ? 'border-blue-500/30 text-blue-400'
                                        : 'border-muted-foreground/30 text-muted-foreground'
                                }`}
                            >
                                {dept.templateSource === 'department'
                                    ? 'Department template'
                                    : 'NordInsight managed'}
                            </Badge>
                        </button>

                        {!isCollapsed && (
                            <div className="border-t border-subtle">
                                {dept.users.map((user) => {
                                    const alignment = computeAlignment(
                                        user.deptPermissions,
                                        dept.templatePermissions
                                    );
                                    return (
                                        <div
                                            key={user._id}
                                            className="flex items-center gap-3 border-b border-subtle/60 px-3 py-2 text-sm last:border-b-0"
                                        >
                                            <div className="min-w-0 shrink-0 sm:w-56">
                                                <p className="truncate font-medium">
                                                    {user.name || '—'}
                                                </p>
                                                <p className="truncate text-xs text-muted-foreground">
                                                    {user.email}
                                                </p>
                                            </div>
                                            <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                                                {user.deptPermissions.map(
                                                    (p) => {
                                                        const inTemplate =
                                                            dept.templatePermissions.includes(
                                                                p
                                                            );
                                                        return (
                                                            <Badge
                                                                key={p}
                                                                variant="outline"
                                                                title={
                                                                    inTemplate
                                                                        ? undefined
                                                                        : 'Not part of the template'
                                                                }
                                                                className={`px-1.5 py-0 text-[10px] ${
                                                                    inTemplate
                                                                        ? ''
                                                                        : 'border-amber-500/40 text-amber-400'
                                                                }`}
                                                            >
                                                                {p}
                                                            </Badge>
                                                        );
                                                    }
                                                )}
                                                {user.deptPermissions.length ===
                                                    0 && (
                                                    <span className="text-xs text-muted-foreground">
                                                        No permissions
                                                    </span>
                                                )}
                                            </div>
                                            {alignment.isAligned ? (
                                                <CircleCheck
                                                    className="h-4 w-4 shrink-0 text-emerald-400"
                                                    aria-label="Matches template"
                                                />
                                            ) : (
                                                <span
                                                    className="flex shrink-0 items-center gap-1 text-xs text-amber-400"
                                                    title={[
                                                        alignment.missing
                                                            .length > 0 &&
                                                            `Missing: ${alignment.missing.join(
                                                                ', '
                                                            )}`,
                                                        alignment.extra.length >
                                                            0 &&
                                                            `Extra: ${alignment.extra.join(
                                                                ', '
                                                            )}`,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' — ')}
                                                >
                                                    <TriangleAlert className="h-4 w-4" />
                                                    {alignment.missing.length >
                                                        0 &&
                                                        `−${alignment.missing.length}`}
                                                    {alignment.missing.length >
                                                        0 &&
                                                        alignment.extra.length >
                                                            0 &&
                                                        ' / '}
                                                    {alignment.extra.length >
                                                        0 &&
                                                        `+${alignment.extra.length}`}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
